<template>
  <div
    v-if="collections.length"
    ref="sliderRef"
    :class="[$style.catalogueCollections, { [$style.catalogueCollectionsInited]: isSliderInited }]"
  >
    <CatalogueSliderHeader
      :icon="collectionsIcon.url"
      :name="collectionsTitle"
      :show-all-title="showAllTitle"
      :is-loading="collections.length && !flickingRef ? false : !flickingRef"
      :show-all-link="useStoreRoot().localePath('/slot-themes')"
      @showAll="goToAllCollections"
    />
    <template v-if="collections.length">
      <Flicking
        v-if="storeRoot.screen.width > 580"
        ref="flickingRef"
        :class="$style.catalogueCollectionsSlider"
        hide-before-init
        :options="{
          bound: true,
          circular: false,
          align: 'prev',
          panelsPerView: slidesPerView,
          inputType: isDesktop ? [] : ['touch', 'mouse'],
        }"
        @ready="sliderOnReady"
      >
        <a
          v-for="(item, id) in collections"
          :key="item.id"
          :href="collectionLink(item)"
          :class="$style.catalogueSliderCollectionsItem"
        >
          <BitCollectionCard
            :image-url="`${item.image?.url}?w=350&q=90&fit=cover&format=webp`"
            :title="item.title"
            @click.prevent="setSelectedCollections(item, id)"
          />
        </a>
      </Flicking>
      <div v-if="storeRoot.screen.width <= 580">
        <a
          v-for="(item, id) in collectionsSliced"
          :key="item.id"
          :href="collectionLink(item)"
          :class="$style.catalogueSliderCollectionsItem"
        >
          <BitCollectionCard
            :image-url="`${item.image?.url}?w=350&q=90&fit=cover&format=webp`"
            :title="item.title"
            @click.prevent="setSelectedCollections(item, id)"
          />
        </a>
      </div>
    </template>
    <CatalogueSliderCollectionsSkeleton v-if="!collections.length || !isSliderInited" />
  </div>
</template>

<script lang="ts" setup>
import { BitCollectionCard } from '@bit/ui-kit';
import { Arrow } from '@egjs/flicking-plugins';
import Flicking, { type FlickingEvents } from '@egjs/vue3-flicking';
import { computed, ref } from 'vue';
import CatalogueSliderCollectionsSkeleton from '~/components/mainPage/catalogueSliderCollectionsSkeleton.vue';
import CatalogueSliderHeader from '~/components/mainPage/catalogueSliderHeader.vue';
import { useStoreGtm } from '~/stores/abstract/gtm';
import { useStoreRoot } from '~/stores/abstract/root';
import '@egjs/vue3-flicking/dist/flicking.css';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import { useStoreSlotTheme } from '~/stores/strapi/slotThemes';
import type { FlickingType } from '~/types/banners';
import type { ISlotThemeData } from '~/types/slots';

defineProps({
  showAllTitle: {
    type: String,
    default: '',
  },
});
defineEmits<{
  (e: 'loaded'): void;
}>();

const storeRoot = useStoreRoot();
const storeCataloguePage = useStoreCataloguePage();

const sliderRef = ref<null | HTMLElement>(null);
const flickingRef = ref<null | FlickingType>(null);
const isSliderInited = ref(false);

const slidesPerView = computed(() => {
  if (storeRoot.screen.width > 1920) {
    return 4;
  } else if (storeRoot.screen.width <= 1920 && storeRoot.screen.width > 1632) {
    return 4;
  } else if (storeRoot.screen.width <= 1632 && storeRoot.screen.width > 1240) {
    return 3;
  } else if (storeRoot.screen.width <= 1240 && storeRoot.screen.width > 768) {
    return 2;
  } else {
    return 2;
  }
});

const collections = computed(() => {
  return useStoreSlotTheme().activeList.filter((item) => {
    return !item.isExploreMoreCollection;
  });
});

const collectionsSliced = computed(() => {
  return collections.value?.slice(0, 3);
});
const collectionsTitle = computed(() => storeCataloguePage.allSpecialCollectionsTitle);
const collectionsIcon = computed(() => storeCataloguePage.allSpecialCollectionsIcon);
const isDesktop = computed(() => usePlatform().isDesktop);

watch(
  () => storeRoot.screen.width,
  (val) => {
    if (val <= 580) {
      isSliderInited.value = true;
    }
  },
  { immediate: true }
);

const sliderOnReady = (event: FlickingEvents['ready']) => {
  const currentTarget = event.currentTarget;
  currentTarget?.addPlugins(
    new Arrow({
      parentEl: sliderRef.value,
    })
  );
  isSliderInited.value = true;
};

const collectionLink = (item: ISlotThemeData) => {
  return useStoreRoot().localePath(
    `${item.isDefaultCategory ? '' : '/slot-themes'}/${item.categoryId}`
  );
};

const setSelectedCollections = async (item: ISlotThemeData, id: number) => {
  useStoreGtm().push({
    event: 'navigation_categories_click',
    event_label: 'collections',
    nav_num: id + 1,
  });
  await navigateTo({
    path: collectionLink(item),
  });
};

const goToAllCollections = () => {
  return navigateTo({ path: useStoreRoot().localePath('/slot-themes') });
};
</script>

<style lang="scss" module src="./catalogueSliderCollections.scss"></style>
